import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

/*
 * The prototype's core panel primitive: a raised bevel box with a Silkscreen
 * title bar. Foundation of nearly every panel across the 7 screens.
 *
 *   default → grey (`--title`) title bar
 *   orange  → warm CTA title bar + orange bevel border (emphasis panels)
 */

interface BevelWindowProps {
  title: ReactNode;
  /** Right-aligned content in the title bar (e.g. "ENDS IN 4 DAYS"). */
  action?: ReactNode;
  /** Small square chip before the title (matches company/team color). */
  chipColor?: string;
  variant?: 'default' | 'orange';
  shadow?: 'sm' | 'lg' | 'none';
  bodyClassName?: string;
  className?: string;
  children: ReactNode;
}

export function BevelWindow({
  title,
  action,
  chipColor,
  variant = 'default',
  shadow = 'sm',
  bodyClassName,
  className,
  children,
}: BevelWindowProps) {
  const isOrange = variant === 'orange';
  return (
    <section
      className={cn(
        isOrange ? 'bevel-orange' : 'bevel',
        'bg-panel',
        shadow === 'lg' && 'shadow-hard',
        shadow === 'sm' && 'shadow-hard-sm',
        className,
      )}
    >
      <header
        className={cn(
          'flex items-center gap-2 px-3 py-1.5',
          isOrange ? 'bg-orange-cta border-b-2 border-b-[#6e3c05]' : 'bg-title',
        )}
      >
        {chipColor ? (
          <span
            className="size-3 shrink-0 border border-black"
            style={{ background: chipColor }}
            aria-hidden
          />
        ) : null}
        <h2
          className={cn(
            'font-display text-[11px] uppercase tracking-wide',
            isOrange ? 'text-white' : 'text-titletext',
          )}
        >
          {title}
        </h2>
        {action ? (
          <div
            className={cn(
              'ml-auto font-mono text-[11px]',
              // text-dim is unreadable on the orange title bar - follow the
              // title and go white there.
              isOrange ? 'text-white' : 'text-dim',
            )}
          >
            {action}
          </div>
        ) : null}
      </header>
      <div className={cn('p-3', bodyClassName)}>{children}</div>
    </section>
  );
}
